/**
 * Learn Backend API Client
 * Handles API calls to Learn Backend for coachees, engagements, etc.
 * Uses shell-app's token management from api.ts
 */

import axios, { type InternalAxiosRequestConfig } from 'axios';
import { getAuthToken } from './api';
import { getAccessToken as getStoredAccessToken, setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken, clearTokens } from '../utils/tokenStorage';

// Learn Backend URL - uses environment variable for production deployment
const rawApiBase =
  import.meta.env.VITE_LEARN_API_BASE_URL ||
  'http://localhost:5002/api/learn';

const LEARN_API_BASE_URL = `${rawApiBase.replace(/\/+$/, '')}/v1/private`;
const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:8000/core/public/v1';

console.log('🎓 Learn Backend API Client Configuration:');
console.log('  - Base URL:', LEARN_API_BASE_URL);
console.log('  - Raw API Base:', rawApiBase);

export const getEffectiveAuthToken = (): string | null => {
  const shellToken = getAuthToken();
  if (shellToken) {
    return shellToken;
  }

  if (typeof window !== 'undefined') {
    const globalToken = (window as any).__SHELL_APP_API__?.getAuthToken?.();
    if (globalToken) {
      return globalToken;
    }
  }

  return localStorage.getItem('access_token') ||
    localStorage.getItem('admin_access_token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token');
};

// Token refresh state for learn API client
let isRefreshingLearn = false;
let learnRefreshSubscribers: ((token: string) => void)[] = [];

const subscribeLearnTokenRefresh = (callback: (token: string) => void) => {
  learnRefreshSubscribers.push(callback);
};

const onLearnTokenRefreshed = (token: string) => {
  learnRefreshSubscribers.forEach(callback => callback(token));
  learnRefreshSubscribers = [];
};

const refreshLearnAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.error('❌ Learn API: No refresh token available');
      return null;
    }

    const response = await axios.post(
      `${PUBLIC_API_BASE_URL}/account/refresh-token`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }
    );

    if (response.data?.access_token) {
      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;

      setStoredAccessToken(newAccessToken);
      if (newRefreshToken) {
        setStoredRefreshToken(newRefreshToken);
      }

      console.log('✅ Learn API: Token refreshed successfully');
      return newAccessToken;
    }

    return null;
  } catch (error) {
    console.error('❌ Learn API: Token refresh failed:', error);
    clearTokens();
    return null;
  }
};

// Create axios instance for Learn Backend
const learnApiClient = axios.create({
  baseURL: LEARN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor to add JWT token from shell-app's token management
learnApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getEffectiveAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔐 Learn Backend Request: ${config.method?.toUpperCase()} ${config.url}`);
    } else {
      console.warn(`⚠️ No token for Learn Backend request: ${config.url}`);
    }

    const impersonationDataStr = localStorage.getItem('impersonation_data');
    if (impersonationDataStr) {
      try {
        const impersonationData = JSON.parse(impersonationDataStr);
        if (impersonationData.sessionToken) {
          config.headers['X-Impersonation-Token'] = impersonationData.sessionToken;
        }
      } catch {
        // Ignore parse errors
      }
    }

    return config;
  },
  (error) => {
    console.error('❌ Learn Backend request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with 401 token refresh handling
learnApiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ Learn Backend Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const { response, config } = error;
    const originalRequest = config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - attempt token refresh and retry
    if (response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // First, check if another client already refreshed the token
      const freshToken = getStoredAccessToken();
      const usedToken = originalRequest.headers?.Authorization?.toString().replace('Bearer ', '');

      if (freshToken && freshToken !== usedToken) {
        // Token was already refreshed by another client — just retry
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        return learnApiClient(originalRequest);
      }

      // Need to refresh ourselves
      if (isRefreshingLearn) {
        // Already refreshing — queue this request
        return new Promise((resolve) => {
          subscribeLearnTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(learnApiClient(originalRequest));
          });
        });
      }

      isRefreshingLearn = true;

      try {
        const newToken = await refreshLearnAccessToken();
        if (newToken) {
          isRefreshingLearn = false;
          onLearnTokenRefreshed(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return learnApiClient(originalRequest);
        } else {
          isRefreshingLearn = false;
          return Promise.reject(error);
        }
      } catch (refreshError) {
        isRefreshingLearn = false;
        return Promise.reject(error);
      }
    }

    console.error(`❌ Learn Backend Error: ${error.response?.status || 'Network'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
    return Promise.reject(error);
  }
);

export default learnApiClient;
export { LEARN_API_BASE_URL };
